export const ROLES = ['SUPER_ADMIN', 'ADMIN', 'SUPERVISOR', 'TECHNICIAN'] as const;

export type Role = (typeof ROLES)[number];

const ALL_ROLES: readonly Role[] = ROLES;
const MANAGEMENT_ROLES: readonly Role[] = ['SUPER_ADMIN', 'ADMIN', 'SUPERVISOR'];
const ADMIN_ROLES: readonly Role[] = ['SUPER_ADMIN', 'ADMIN'];

type RouteRule = {
  pattern: RegExp;
  roles: readonly Role[];
};

export type PermissionAction =
  | 'assets.manage'
  | 'assets.delete'
  | 'pm.manage'
  | 'workOrders.create'
  | 'workOrders.delete'
  | 'inventory.manage'
  | 'inventory.delete'
  | 'clients.manage'
  | 'users.manage'
  | 'customerRequests.delete';

const ROUTE_RULES: RouteRule[] = [
  { pattern: /^\/dashboard$/, roles: MANAGEMENT_ROLES },
  { pattern: /^\/simulator$/, roles: ALL_ROLES },
  { pattern: /^\/assets(?:\/[^/]+)?$/, roles: ALL_ROLES },
  { pattern: /^\/pm(?:\/[^/]+)?$/, roles: ALL_ROLES },
  { pattern: /^\/work-orders(?:\/[^/]+)?$/, roles: ALL_ROLES },
  { pattern: /^\/dispatch$/, roles: ALL_ROLES },
  { pattern: /^\/inventory$/, roles: ALL_ROLES },
  { pattern: /^\/technicians$/, roles: MANAGEMENT_ROLES },
  { pattern: /^\/technicians\/[^/]+$/, roles: ALL_ROLES },
  { pattern: /^\/projects(?:\/[^/]+)?$/, roles: MANAGEMENT_ROLES },
  { pattern: /^\/quotes(?:\/[^/]+)?$/, roles: MANAGEMENT_ROLES },
  { pattern: /^\/invoices$/, roles: ADMIN_ROLES },
  { pattern: /^\/invoices\/[^/]+$/, roles: MANAGEMENT_ROLES },
  { pattern: /^\/clients$/, roles: MANAGEMENT_ROLES },
  { pattern: /^\/settings\/users$/, roles: ADMIN_ROLES },
  { pattern: /^\/settings\/permissions$/, roles: ADMIN_ROLES },
  { pattern: /^\/settings\/audit-log$/, roles: ['SUPER_ADMIN'] },
  { pattern: /^\/settings\/password$/, roles: ALL_ROLES },
  { pattern: /^\/reports$/, roles: MANAGEMENT_ROLES },
  { pattern: /^\/inbox$/, roles: MANAGEMENT_ROLES },
  { pattern: /^\/time-clock$/, roles: ALL_ROLES },
];

const ACTION_RULES: Record<PermissionAction, readonly Role[]> = {
  'assets.manage': MANAGEMENT_ROLES,
  'assets.delete': ['SUPER_ADMIN'],
  'pm.manage': MANAGEMENT_ROLES,
  'workOrders.create': MANAGEMENT_ROLES,
  'workOrders.delete': ['SUPER_ADMIN'],
  'inventory.manage': MANAGEMENT_ROLES,
  'inventory.delete': ['SUPER_ADMIN'],
  'clients.manage': ADMIN_ROLES,
  'users.manage': ADMIN_ROLES,
  'customerRequests.delete': ['SUPER_ADMIN'],
};

export function normalizeRole(role?: string | null): Role | null {
  return ROLES.includes(role as Role) ? (role as Role) : null;
}

export function hasRole(role: string | undefined | null, roles: readonly Role[]): boolean {
  const normalized = normalizeRole(role);
  return normalized ? roles.includes(normalized) : false;
}

export function getDefaultPathForRole(role?: string | null): string {
  const normalized = normalizeRole(role);
  if (normalized === 'TECHNICIAN') return '/work-orders';
  return '/dashboard';
}

export function canAccessPath(role: string | undefined | null, pathname: string): boolean {
  const normalized = normalizeRole(role);
  if (!normalized) return false;

  const cleanPath = pathname.replace(/\/+$/, '') || '/';
  const rule = ROUTE_RULES.find((item) => item.pattern.test(cleanPath));
  return rule ? rule.roles.includes(normalized) : false;
}

export function canPerform(role: string | undefined | null, action: PermissionAction): boolean {
  return hasRole(role, ACTION_RULES[action]);
}
